<template>
  <div>
    <PageHeader :title="t('title')">
      <CreateButton @click="showCreateModal = true">{{ t("create") }}</CreateButton>
    </PageHeader>
    <SearchToolbar
      class="mb-6"
      :placeholder="t('search_placeholder')"
      :initial-keywords="keywords"
      @update-keyword-search="updateKeywordSearch"
    />
    <Loading v-if="loading" />
    <div v-else-if="insurerManagers.length === 0" class="text-center mt-4 py-2">
      <p>{{ t("no_results") }}</p>
    </div>
    <div v-else>
      <Table :columns="columns" :data="formattedData" isAccessible @view="goToShow">
        <template #signedUp="{ row }">
          <FieldSpan
            :style-to-add="row.signedUp ? 'text-green-800 bg-green-200' : 'text-red-800 bg-red-200'"
            :value="row.signedUp ? t('signed_up_yes') : t('signed_up_no')"
          />
        </template>
      </Table>
      <Pagination
        :currentPage="currentPage"
        :totalPages="totalPages"
        :totalCount="totalCount"
        @changePage="handleChangePage"
      />
    </div>

    <InsurerManagerFormModal v-if="showCreateModal" @close="showCreateModal = false" @created="handleCreated" />
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { useI18n } from "vue-i18n";
import { PageHeader, Loading, Table, Pagination, FieldSpan, CreateButton, SearchToolbar } from "@estaly/ui";
import InsurerManagerFormModal from "./InsurerManagerFormModal.vue";

export default {
  components: {
    PageHeader,
    Loading,
    Table,
    Pagination,
    FieldSpan,
    CreateButton,
    SearchToolbar,
    InsurerManagerFormModal,
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  data() {
    return {
      loading: true,
      keywords: null,
      showCreateModal: false,
      columns: [],
    };
  },

  computed: {
    ...mapState("insurerManagers", {
      insurerManagers: "index",
      currentPage: "currentPage",
      totalPages: "totalPages",
      totalCount: "totalCount",
    }),

    formattedData() {
      return this.insurerManagers.map((insurerManager) => ({
        id: insurerManager.id,
        email: insurerManager.email,
        fullName: [insurerManager.firstName, insurerManager.lastName].filter(Boolean).join(" "),
        translatedRole: this.t(`role_${insurerManager.role}`),
        signedUp: insurerManager.signedUp,
      }));
    },
  },

  async created() {
    this.buildColumns();
    await this.retrieveInsurerManagers();
  },

  methods: {
    ...mapActions("insurerManagers", { fetchInsurerManagers: "fetchIndex" }),

    buildColumns() {
      this.columns = [
        { label: this.t("column_id"), field: "id", widthClass: "w-16" },
        { label: this.t("column_email"), field: "email" },
        { label: this.t("column_full_name"), field: "fullName" },
        { label: this.t("column_role"), field: "translatedRole" },
        { label: this.t("column_signed_up"), field: "signedUp", slot: "signedUp" },
      ];
    },

    async retrieveInsurerManagers(page = 1) {
      this.loading = true;
      let response;
      try {
        response = await this.fetchInsurerManagers({ page, keywords: this.keywords });
      } finally {
        // An outdated response must not stop the loader of the request still pending.
        if (response?.isLatest !== false) this.loading = false;
      }
    },

    handleChangePage(page) {
      this.retrieveInsurerManagers(page);
    },

    updateKeywordSearch(keywords) {
      this.keywords = keywords;
      this.retrieveInsurerManagers();
    },

    async handleCreated() {
      this.showCreateModal = false;
      await this.retrieveInsurerManagers(this.currentPage);
    },

    goToShow(row) {
      this.$router.push(`/internal/users/${row.id}`);
    },
  },
};
</script>

<i18n src="./InsurerManagerList.i18n.json"></i18n>
